import { LazyAPIImage } from '@components/common/image'
import { useProfileContext } from '@contexts/profile'
import { CodingContestType, HackathonType } from '@lib/types/profile'
import Link from 'next/link'
import React, { useMemo } from 'react'

type CompetitionType = 'hackathons' | 'coding-contests'

type CompetitionCardProps = {
  competition: HackathonType | CodingContestType
  competitionType: CompetitionType
}

export const CompetitionCard = React.memo(
  ({ competition, competitionType }: CompetitionCardProps) => (
    <div className="flex border border-grey-500 space-x-3 rounded-lg p-5 lg:p-8 leading-tight">
      <LazyAPIImage
        src={competition?.orgLogo}
        className="object-contain w-16 lg:w-20 h-16 lg:h-20"
        alt={competition.orgTitle}
      />
      <div className="flex flex-col space-y-3">
        <h3 className="text-lg font-600">{competition?.title}</h3>
        <p className="text-grey-800">Hosted by: {competition?.orgTitle}</p>
        {competition?.slug && (
          <div className="flex space-x-5">
            {competitionType === 'coding-contests' && (
              <Link href={`/competitions/${competition.slug}/leaderboard/`}>
                <a className="text-orange hover:underline font-600">
                  View leaderboard
                </a>
              </Link>
            )}
            <Link href={`/competitions/${competition.slug}/`}>
              <a className="text-orange hover:underline font-600">
                View competition
              </a>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
)

type Props = {
  competitionType: CompetitionType
}

const Competitions = ({ competitionType }: Props) => {
  const { profileDetails } = useProfileContext()
  const competitions: HackathonType[] | CodingContestType[] = useMemo(
    () =>
      competitionType === 'hackathons'
        ? profileDetails?.hackathons
        : profileDetails?.codingContests,
    []
  )
  const viewMoreLink = useMemo(
    () => `/@${profileDetails?.username}/${competitionType}`,
    []
  )
  if (!competitions || competitions?.length === 0) return null
  return (
    <div className="flex flex-col p-5 lg:p-10 bg-white rounded-lg text-grey items-start">
      <h2 className="text-xl font-600 text-grey max-w-2xl mb-4">
        {competitionType === 'hackathons' ? 'Hackathons' : 'Coding contests'}
      </h2>
      <div className="grid gap-6 lg:grid-cols-2 w-full mb-4">
        {competitions.map((competition) => (
          <CompetitionCard
            key={competition.slug}
            competition={competition}
            competitionType={competitionType}
          />
        ))}
      </div>
      <Link href={viewMoreLink}>
        <a className="text-orange hover:underline font-600">View more</a>
      </Link>
    </div>
  )
}

export default React.memo(Competitions)
